// Single source of truth for tenant-customizable color tokens.
// Values resolve live from CSS custom properties set on document.documentElement
// by ThemeContext (see contexts/ThemeContext.jsx) from Tenant.branding, loaded
// once at app bootstrap. The fallback in each var() is the platform default —
// components render correctly before branding has loaded or for tenants who
// haven't customized anything.
//
// Layout/spacing/structure is NOT part of this file — it stays with each
// component. This file only holds the values a company can actually rebrand.

export const theme = {
  colors: {
    primary:            'var(--primary-color, #3b82f6)',
    primaryRgb:         'var(--primary-color-rgb, 59, 130, 246)',
    secondary:          'var(--secondary-color, #94a3b8)',
    secondaryRgb:       'var(--secondary-color-rgb, 148, 163, 184)',
    accent:             'var(--accent-color, #8b5cf6)',
    accentRgb:          'var(--accent-color-rgb, 139, 92, 246)',
    tileBackground:     'var(--tile-background, #0f172a)',
    tileBackgroundRgb:  'var(--tile-background-rgb, 15, 23, 42)',
    tileBorder:         'var(--tile-border, #3b82f6)',
    tileBorderRgb:      'var(--tile-border-rgb, 59, 130, 246)',
    sidebarBackground:    'var(--sidebar-background, #0f172a)',
    sidebarBackgroundRgb: 'var(--sidebar-background-rgb, 15, 23, 42)',
    sidebarText:          'var(--sidebar-text, #94a3b8)',
    sidebarTextRgb:       'var(--sidebar-text-rgb, 148, 163, 184)',

    // The outermost app-shell backdrop (behind the sidebar and every page) —
    // distinct from the primary/accent tinted gradient panels layered on top
    // of it (see textOnPageBackground below), which is a separate concept.
    // shellBackgroundBlend is shellBackground mixed with shellAccent (via
    // mixHex below) — a real two-hue gradient stop, not just a lighter shade.
    // shellAccent defaults to accentColor (see ThemeContext/CustomizationPage)
    // if never explicitly set, but is a distinct, independently-overridable
    // field — changing accentColor for buttons/links doesn't silently also
    // change the background gradient unless that's actually intended.
    shellBackground:      'var(--shell-background, #0f172a)',
    shellAccent:          'var(--shell-accent, #7c3aed)',
    shellBackgroundBlend: 'var(--shell-background-blend, #46298c)',

    // Auto-computed (by ThemeContext, see pickReadableText below) — never stored
    // on Tenant.branding. Whichever of light/dark text has higher contrast
    // against the corresponding background.
    textOnPrimary:           'var(--text-on-primary, #f8fafc)',
    textOnTileBackground:    'var(--text-on-tile-background, #f8fafc)',
    textOnSidebarBackground: 'var(--text-on-sidebar-background, #f8fafc)',
    textOnPageBackground:    'var(--text-on-page-background, #94a3b8)',
  },
};

// Builds a translucent variant of a themed color, e.g. rgba(theme.colors.primaryRgb, 0.2)
// -> "rgba(var(--primary-color-rgb, 59, 130, 246), 0.2)", which stays live as the
// underlying CSS variable changes.
export const rgba = (themeRgb, alpha) => `rgba(${themeRgb}, ${alpha})`;

// WCAG 2.x relative luminance + contrast ratio — picks whichever of a light or
// dark text color has the higher contrast ratio against a given background hex,
// rather than a rough "luminance > 50%" heuristic.
const READABLE_LIGHT = '#f8fafc';
const READABLE_DARK = '#0f172a';

function relativeLuminance(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return null;
  const [r, g, b] = [m[1], m[2], m[3]].map(h => {
    const s = parseInt(h, 16) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(l1, l2) {
  const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
}

const LUM_LIGHT = relativeLuminance(READABLE_LIGHT);
const LUM_DARK = relativeLuminance(READABLE_DARK);

export function pickReadableText(bgHex) {
  const bgL = relativeLuminance(bgHex);
  if (bgL === null) return null;
  return contrastRatio(bgL, LUM_LIGHT) >= contrastRatio(bgL, LUM_DARK) ? READABLE_LIGHT : READABLE_DARK;
}

// Midpoint of two hex colors. Used both to approximate a two-stop gradient
// as one representative color for contrast purposes (pickPageBackgroundText
// below), and directly as a real gradient stop (shellBackgroundBlend, see
// ThemeContext and CustomizationPage — both call this same function so the
// live-editing preview and the post-save render can never disagree).
export function mixHex(hexA, hexB) {
  const parse = hex => /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  const a = parse(hexA), b = parse(hexB);
  if (!a || !b) return null;
  const mixed = [1, 2, 3].map(i => Math.round((parseInt(a[i], 16) + parseInt(b[i], 16)) / 2));
  return '#' + mixed.map(c => c.toString(16).padStart(2, '0')).join('');
}

export function pickPageBackgroundText(primaryHex, accentHex) {
  const mixed = mixHex(primaryHex, accentHex);
  return mixed ? pickReadableText(mixed) : null;
}

// The one place mapping a token key to its CSS custom property name — shared
// by every live-preview call site (CustomizationPage's own editing, and
// chat-driven changes via useChat) so they can never drift into two
// different implementations of "what var does this token control."
export const CSS_VAR_NAMES = {
  primaryColor: '--primary-color',
  secondaryColor: '--secondary-color',
  accentColor: '--accent-color',
  tileBackground: '--tile-background',
  tileBorder: '--tile-border',
  sidebarBackground: '--sidebar-background',
  sidebarText: '--sidebar-text',
  shellBackground: '--shell-background',
  shellBackgroundAccent: '--shell-accent',
};

export const SHELL_GRADIENT_KEYS = ['shellBackground', 'accentColor', 'shellBackgroundAccent'];
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

// Recomputes just the shell gradient's derived blend stop from a full
// effective-theme object — used both by previewThemeToken below (after
// setting the one field that changed) and standalone, for the one case
// where a field being reset has no default of its own to restore
// (shellBackgroundAccent can be legitimately unset at both the personal AND
// company level, correctly falling back to accentColor - there's no hex to
// hand previewThemeToken for that field, but the blend still needs to
// reflect the fallback).
export function recomputeShellBlend(effective) {
  const gradientAccent = effective.shellBackgroundAccent || effective.accentColor;
  const blend = mixHex(effective.shellBackground, gradientAccent);
  if (blend) document.documentElement.style.setProperty('--shell-background-blend', blend);
}

// Live-previews a single token change against document.documentElement,
// given the currently-effective theme (so the shell gradient's derived
// blend stop — which depends on shellBackground AND accentColor/
// shellBackgroundAccent together — recomputes correctly even though only
// one of those three actually changed here). The exact same call
// ThemeContext's applyBranding makes after a real save, so a live preview
// and the eventual persisted render can never show two different things.
export function previewThemeToken(effective, tokenKey, hex) {
  if (!HEX_COLOR.test(hex)) return;
  const varName = CSS_VAR_NAMES[tokenKey];
  if (varName) document.documentElement.style.setProperty(varName, hex);
  if (SHELL_GRADIENT_KEYS.includes(tokenKey)) {
    recomputeShellBlend({ ...effective, [tokenKey]: hex });
  }
}
